import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, TextField, Grid, Card, CardContent, Typography, CardActions, Button,
  CircularProgress, Pagination, Chip, InputAdornment, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Snackbar, Alert, MenuItem, Tabs, Tab,
  CardActionArea, Select, FormControl, InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { searchCurseForge, installCurseForgeMod, getCurseForgeMod } from '../api';

const MOD_LOADER_IDS = {
    'FORGE': 1,
    'FABRIC': 4,
    'QUILT': 5,
    'NEOFORGE': 6
};

const MOD_LOADER_NAMES = {
    1: 'Forge',
    4: 'Fabric',
    5: 'Quilt',
    6: 'NeoForge'
};

export default function CurseForgeBrowser({ serverName, serverVersion, serverType, open, onClose, onInstall, installedMods = [] }) {
  const [mods, setMods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [installing, setInstalling] = useState({}); // Track installing state by mod ID
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [selectedLoader, setSelectedLoader] = useState(serverType || 'Any');
  const [selectedGameVersion, setSelectedGameVersion] = useState(serverVersion || '');
  const [category, setCategory] = useState(6); // 6 = Mods, 17 = Worlds
  const [sortField, setSortField] = useState(2); // 2 = Popularity, 6 = Total Downloads, 1 = Featured
  const [selectedMod, setSelectedMod] = useState(null); // For detail view
  const [modDetail, setModDetail] = useState(null); // Detailed data
  const [tempInstalled, setTempInstalled] = useState([]); // Temporarily track installed mods/worlds in this session
  const [selectedFile, setSelectedFile] = useState(null); // Selected file for installation
  
  // Detail view filters
  const [detailGameVersion, setDetailGameVersion] = useState('All');
  const [detailModLoader, setDetailModLoader] = useState('All');

  const pageSize = 12;

  const getUniqueVersions = useCallback((files) => {
      const versions = new Set();
      if (files) {
          files.forEach(f => {
              f.gameVersions.forEach(v => {
                  if (/^\d+\.\d+(\.\d+)?$/.test(v)) versions.add(v);
              });
          });
      }
      return Array.from(versions).sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));
  }, []);

  const getUniqueLoaders = useCallback((mod) => {
      if (!mod || !mod.latestFiles) return [];
      const fileIds = new Set(mod.latestFiles.map(f => f.id));
      const loaders = new Set();
      
      if (mod.latestFilesIndexes) {
          mod.latestFilesIndexes.forEach(idx => {
              if (fileIds.has(idx.fileId) && MOD_LOADER_NAMES[idx.modLoader]) {
                  loaders.add(MOD_LOADER_NAMES[idx.modLoader]);
              }
          });
      }
      
      // Fallback if no indexes or empty
      if (loaders.size === 0) {
           mod.latestFiles.forEach(f => {
              f.gameVersions.forEach(v => {
                  if (['Forge', 'Fabric', 'Quilt', 'NeoForge'].includes(v)) loaders.add(v);
              });
          });
      }
      
      return Array.from(loaders).sort();
  }, []);

  const fetchMods = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let response;
      
      const loader = selectedLoader !== 'Any' ? selectedLoader : null;
      const version = selectedGameVersion || null;

      response = await searchCurseForge(searchQuery, version, loader, category, sortField, pageSize, offset);

      if (response.data && response.data.data) {
        setMods(response.data.data);
        const total = response.data.pagination.totalCount;
        setTotalPages(Math.ceil(total / pageSize));
      }
    } catch (error) {
      console.error("Failed to fetch mods:", error);
      setSnackbar({ open: true, message: 'Failed to fetch mods', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, selectedGameVersion, selectedLoader, category, sortField]);

  useEffect(() => {
    if (open && !selectedMod) {
      fetchMods();
    }
  }, [open, fetchMods, selectedMod]);

  // Reset temp installed when dialog opens
  useEffect(() => {
      if (open) {
          setTempInstalled([]);
      }
  }, [open]);

  // Update selectedLoader and selectedGameVersion when serverType/serverVersion changes
  useEffect(() => {
      if (serverType) {
          setSelectedLoader(serverType);
      }
      if (serverVersion) {
          setSelectedGameVersion(serverVersion);
      }
  }, [serverType, serverVersion]);

  // Fetch mod details when a mod is selected
  useEffect(() => {
    if (selectedMod) {
      const fetchDetail = async () => {
        setLoading(true);
        try {
          const { data } = await getCurseForgeMod(selectedMod.id);
          setModDetail(data);
          
          // Initialize detail filters with server info if available, otherwise All
          // Check if serverVersion exists in the mod's versions
          const uniqueVersions = getUniqueVersions(data.latestFiles);
          const hasServerVersion = uniqueVersions.includes(serverVersion);
          setDetailGameVersion(hasServerVersion ? serverVersion : 'All');
          
          const uniqueLoaders = getUniqueLoaders(data);
          // Check if serverType exists in uniqueLoaders (case insensitive)
          const matchingLoader = uniqueLoaders.find(l => serverType && l.toUpperCase() === serverType.toUpperCase());
          setDetailModLoader(matchingLoader ? matchingLoader : 'All');

        } catch (error) {
          console.error("Failed to fetch mod detail:", error);
          setSnackbar({ open: true, message: 'Failed to load mod details', severity: 'error' });
        } finally {
          setLoading(false);
        }
      };
      fetchDetail();
    } else {
      setModDetail(null);
      setSelectedFile(null);
      setDetailGameVersion('All');
      setDetailModLoader('All');
    }
  }, [selectedMod, serverVersion, serverType, getUniqueVersions, getUniqueLoaders]);

  // Update selected file when filters or modDetail changes
  useEffect(() => {
      if (!modDetail) return;

      const filteredFiles = modDetail.latestFiles.filter(f => {
          const hasVersion = detailGameVersion === 'All' || f.gameVersions.includes(detailGameVersion);
          
          let hasLoader = false;
          if (detailModLoader === 'All') {
              hasLoader = true;
          } else {
              const targetLoaderId = MOD_LOADER_IDS[detailModLoader.toUpperCase()];
              if (targetLoaderId && modDetail.latestFilesIndexes) {
                  hasLoader = modDetail.latestFilesIndexes.some(idx => idx.fileId === f.id && idx.modLoader === targetLoaderId);
              }
              if (!hasLoader) {
                  hasLoader = f.gameVersions.includes(detailModLoader);
              }
          }
          
          return hasVersion && hasLoader;
      });

      if (filteredFiles.length > 0) {
          setSelectedFile(filteredFiles[0]);
      } else {
          setSelectedFile(null);
      }

  }, [modDetail, detailGameVersion, detailModLoader]);


  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchMods();
  };

  const handleCategoryChange = (event, newValue) => {
    setCategory(newValue);
    setPage(1);
    setSelectedMod(null);
  };

  const handleInstall = async (mod, fileToInstall, e) => {
    if (e) e.stopPropagation(); // Prevent card click

    if (category === 17) {
        if (!window.confirm("Installing a world will REPLACE the current world folder. This action cannot be undone. Are you sure you want to proceed?")) {
            return;
        }
    }

    if (!fileToInstall) {
        setSnackbar({ open: true, message: "No file selected.", severity: 'warning' });
        return;
    }

    setInstalling(prev => ({ ...prev, [mod.id]: true }));

    try {
      await installCurseForgeMod(serverName, {
        modId: mod.id,
        fileId: fileToInstall.id,
        fileName: fileToInstall.fileName,
        downloadUrl: fileToInstall.downloadUrl,
        classId: category
      });
      if (onInstall) onInstall(fileToInstall.fileName);
      
      // Mark as temporarily installed
      setTempInstalled(prev => [...prev, mod.id]);
      
      setSnackbar({ open: true, message: `Installed ${mod.name}`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Failed to install ${mod.name}: ${error.message}`, severity: 'error' });
    } finally {
      setInstalling(prev => ({ ...prev, [mod.id]: false }));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isInstalled = (mod) => {
      // Check temp installed first
      if (tempInstalled.includes(mod.id)) return true;

      // Worlds are not tracked in installedMods, so always return false for worlds unless temp installed
      if (category === 17) return false;

      if (!installedMods) return false;
      return mod.latestFiles.some(file => installedMods.includes(file.fileName));
  };

  const handleCardClick = (mod) => {
    setSelectedMod(mod);
  };

  const handleBack = () => {
    setSelectedMod(null);
  };

  const getFileLabel = (file) => {
      let version = file.gameVersions.filter(v => /^\d+\.\d+(\.\d+)?$/.test(v)).join(', ');
      let loader = file.gameVersions.filter(v => ['Forge', 'Fabric', 'Quilt', 'NeoForge'].includes(v)).join(', ');
      return `${file.displayName} (${version} - ${loader})`;
  };
    return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedMod ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleBack} size="small"><ArrowBackIcon /></IconButton>
              {selectedMod.name}
            </Box>
          ) : (
            `CurseForge Browser ${selectedGameVersion ? `(Version: ${selectedGameVersion})` : ''}`
          )}
        </DialogTitle>
        <DialogContent>
          {!selectedMod && (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={category} onChange={handleCategoryChange} aria-label="curseforge category tabs">
                  <Tab label="Mods" value={6} />
                  <Tab label="Worlds" value={17} />
                </Tabs>
              </Box>

              <Box sx={{ mb: 3, mt: 1 }}>
                <form onSubmit={handleSearch}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder={category === 6 ? "Search mods..." : "Search worlds..."}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                    <IconButton type="submit">
                                        <SearchIcon />
                                    </IconButton>
                                    </InputAdornment>
                                ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField
                                select
                                fullWidth
                                label="Sort By"
                                value={sortField}
                                onChange={(e) => {
                                    setSortField(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <MenuItem value={1}>Featured</MenuItem>
                                <MenuItem value={2}>Popularity</MenuItem>
                                <MenuItem value={6}>Total Downloads</MenuItem>
                                <MenuItem value={3}>Last Updated</MenuItem>
                                <MenuItem value={4}>Name</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                label="Game Version"
                                value={selectedGameVersion}
                                onChange={(e) => {
                                    setSelectedGameVersion(e.target.value);
                                    setPage(1);
                                }}
                                placeholder="e.g. 1.20.1"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                select
                                fullWidth
                                label="Mod Loader"
                                value={selectedLoader}
                                onChange={(e) => {
                                    setSelectedLoader(e.target.value);
                                    setPage(1); 
                                }}
                                disabled={category === 17} 
                            >
                                <MenuItem value="Any">Any</MenuItem>
                                <MenuItem value="FORGE">Forge</MenuItem>
                                <MenuItem value="FABRIC">Fabric</MenuItem>
                                <MenuItem value="QUILT">Quilt</MenuItem>
                                <MenuItem value="NEOFORGE">NeoForge</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </form>
              </Box>
            </>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : selectedMod && modDetail ? (
            // Detail View
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <img 
                    src={modDetail.logo?.url || 'https://via.placeholder.com/300x200?text=No+Image'} 
                    alt={modDetail.name}
                    style={{ width: '100%', borderRadius: 8 }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Created by: {modDetail.authors?.map(a => a.name).join(', ')}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Downloads: {modDetail.downloadCount.toLocaleString()}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Updated: {new Date(modDetail.dateModified).toLocaleDateString()}</Typography>
                  </Box>
                  
                  <Box sx={{ mt: 3 }}>
                      {/* Detail View Filters */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                  <InputLabel>Game Version</InputLabel>
                                  <Select
                                      value={detailGameVersion}
                                      label="Game Version"
                                      onChange={(e) => setDetailGameVersion(e.target.value)}
                                  >
                                      <MenuItem value="All">All</MenuItem>
                                      {getUniqueVersions(modDetail.latestFiles).map(v => {
                                          const isRecommended = v === serverVersion;
                                          return (
                                              <MenuItem 
                                                  key={v} 
                                                  value={v}
                                                  sx={{ 
                                                      fontWeight: isRecommended ? 'bold' : 'normal',
                                                      color: isRecommended ? 'success.main' : 'text.primary'
                                                  }}
                                              >
                                                  {v} {isRecommended && "(Recommended)"}
                                              </MenuItem>
                                          );
                                      })}
                                  </Select>
                              </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                  <InputLabel>Mod Loader</InputLabel>
                                  <Select
                                      value={detailModLoader}
                                      label="Mod Loader"
                                      onChange={(e) => setDetailModLoader(e.target.value)}
                                  >
                                      <MenuItem value="All">All</MenuItem>
                                      {getUniqueLoaders(modDetail).map(l => {
                                          const isRecommended = serverType && l.toUpperCase() === serverType.toUpperCase();
                                          return (
                                              <MenuItem 
                                                  key={l} 
                                                  value={l}
                                                  sx={{ 
                                                      fontWeight: isRecommended ? 'bold' : 'normal',
                                                      color: isRecommended ? 'success.main' : 'text.primary'
                                                  }}
                                              >
                                                  {l} {isRecommended && "(Recommended)"}
                                              </MenuItem>
                                          );
                                      })}
                                  </Select>
                              </FormControl>
                          </Grid>
                      </Grid>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel id="file-select-label">Select File</InputLabel>
                          <Select
                              labelId="file-select-label"
                              value={selectedFile ? selectedFile.id : ''}
                              label="Select File"
                              onChange={(e) => {
                                  const file = modDetail.latestFiles.find(f => f.id === e.target.value);
                                  setSelectedFile(file);
                              }}
                          >
                              {modDetail.latestFiles
                                  .filter(f => {
                                      const hasVersion = detailGameVersion === 'All' || f.gameVersions.includes(detailGameVersion);
                                      
                                      let hasLoader = false;
                                      if (detailModLoader === 'All') {
                                          hasLoader = true;
                                      } else {
                                          const targetLoaderId = MOD_LOADER_IDS[detailModLoader.toUpperCase()];
                                          if (targetLoaderId && modDetail.latestFilesIndexes) {
                                              hasLoader = modDetail.latestFilesIndexes.some(idx => idx.fileId === f.id && idx.modLoader === targetLoaderId);
                                          }
                                          if (!hasLoader) {
                                              hasLoader = f.gameVersions.includes(detailModLoader);
                                          }
                                      }

                                      return hasVersion && hasLoader;
                                  })
                                  .map((file) => {
                                      return (
                                          <MenuItem 
                                              key={file.id} 
                                              value={file.id}
                                          >
                                              {getFileLabel(file)}
                                          </MenuItem>
                                      );
                                  })}
                          </Select>
                      </FormControl>

                     {isInstalled(modDetail) ? (
                         <Button 
                          startIcon={<CheckCircleIcon />} 
                          fullWidth
                          variant="outlined"
                          color="success"
                          disabled
                          size="large"
                        >
                          Installed
                        </Button>
                      ) : (
                        <Button 
                          startIcon={installing[modDetail.id] ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />} 
                          onClick={(e) => handleInstall(modDetail, selectedFile, e)}
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={installing[modDetail.id] || !selectedFile}
                        >
                          {installing[modDetail.id] ? 'Installing...' : 'Install'}
                        </Button>
                      )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" gutterBottom>Description</Typography>
                  <Box 
                    sx={{ 
                      '& img': { maxWidth: '100%', height: 'auto' },
                      '& a': { color: 'primary.main' }
                    }}
                    dangerouslySetInnerHTML={{ __html: modDetail.descriptionHtml || modDetail.summary }} 
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            // List View
            <>
              <Grid container spacing={2}>
                {mods.map((mod) => {
                  const installed = isInstalled(mod);
                  // Find best file for list view quick install
                  let bestFile = null;
                  if (mod.latestFilesIndexes && mod.latestFilesIndexes.length > 0) {
                      const compatibleIndex = mod.latestFilesIndexes.find(idx => idx.gameVersion === selectedGameVersion);
                      if (compatibleIndex) {
                          bestFile = mod.latestFiles.find(f => f.id === compatibleIndex.fileId);
                      }
                  }
                  
                  if (!bestFile) {
                      bestFile = mod.latestFiles.find(f => f.gameVersions.includes(selectedGameVersion));
                  }

                  if (!bestFile && mod.latestFiles.length > 0) {
                      bestFile = mod.latestFiles[mod.latestFiles.length - 1]; 
                  }

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={mod.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardActionArea onClick={() => handleCardClick(mod)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Box sx={{ position: 'relative', pt: '56.25%', width: '100%' }}>
                              <img 
                                  src={mod.logo?.thumbnailUrl || 'https://via.placeholder.com/300x200?text=No+Image'} 
                                  alt={mod.name}
                                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                          </Box>
                          <CardContent sx={{ width: '100%' }}>
                            <Typography gutterBottom variant="h6" component="div" noWrap>
                              {mod.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                            }}>
                              {mod.summary}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Chip label={`Downloads: ${mod.downloadCount.toLocaleString()}`} size="small" variant="outlined" />
                            </Box>
                          </CardContent>
                        </CardActionArea>
                        <CardActions>
                          {installed ? (
                             <Button 
                              size="small" 
                              startIcon={<CheckCircleIcon />} 
                              fullWidth
                              variant="outlined"
                              color="success"
                              disabled
                            >
                              Installed
                            </Button>
                          ) : (
                            <Button 
                              size="small" 
                              startIcon={installing[mod.id] ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />} 
                              onClick={(e) => handleInstall(mod, bestFile, e)}
                              fullWidth
                              variant="contained"
                              disabled={installing[mod.id]}
                            >
                              {installing[mod.id] ? 'Installing...' : 'Install'}
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              
              {mods.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination 
                      count={totalPages > 100 ? 100 : totalPages} // Cap at 100 pages for now
                      page={page} 
                      onChange={(e, v) => setPage(v)} 
                      color="primary" 
                  />
                  </Box>
              )}
               {mods.length === 0 && (
                  <Typography align="center" sx={{ mt: 4 }}>No mods found.</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}